/**
 * Cover art, or a cover we draw ourselves.
 *
 * The fallback is the mockup's generated artwork, carried over unchanged —
 * DESIGN.md always intended the same visual to double as the missing-art
 * placeholder, and it is why a release with no cover in the archive still
 * looks like a record rather than like a bug. It is deterministic in the
 * album's own title and artist, so the same album is always the same colours
 * and never flickers between renders.
 *
 * `onError` falls back too: a Cover Art Archive URL that 404s after we cached
 * it (a takedown, an outage) steps down to the next size we know of and then
 * to the drawn cover, instead of a broken image icon.
 */
import { useState } from 'react'

const PATTERNS = {
  orbits: () => (
    <>
      {[46, 34, 22].map((r, i) => (
        <circle
          key={r}
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth={i === 1 ? 2.5 : 1}
        />
      ))}
      <circle cx="72" cy="34" r="5" fill="rgba(255,255,255,0.85)" />
    </>
  ),
  waves: () => (
    <>
      {[38, 50, 62, 74].map((y, i) => (
        <path
          key={y}
          d={`M -5 ${y} Q 25 ${y - 12}, 50 ${y} T 105 ${y}`}
          fill="none"
          stroke={`rgba(255,255,255,${0.5 - i * 0.09})`}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      ))}
    </>
  ),
  beams: () => (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <rect
          key={i}
          x={12 + i * 18}
          y={-20}
          width="7"
          height="140"
          rx="3.5"
          transform="rotate(18 50 50)"
          fill={`rgba(255,255,255,${0.42 - i * 0.07})`}
        />
      ))}
    </>
  ),
  facets: () => (
    <>
      <polygon points="50,14 86,80 14,80" fill="rgba(255,255,255,0.22)" />
      <polygon points="50,32 74,80 26,80" fill="rgba(255,255,255,0.30)" />
      <polygon points="50,52 63,80 37,80" fill="rgba(255,255,255,0.45)" />
    </>
  ),
  pulse: () => (
    <>
      {[10, 20, 31, 43].map((r, i) => (
        <circle
          key={r}
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={`rgba(255,255,255,${0.55 - i * 0.12})`}
          strokeWidth={5 - i}
        />
      ))}
    </>
  ),
  grid: () => (
    <>
      {[22, 40, 58, 76].flatMap((x) =>
        [22, 40, 58, 76].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="4.5" fill="rgba(255,255,255,0.4)" />
        )),
      )}
    </>
  ),
}

const PATTERN_NAMES = Object.keys(PATTERNS)

/** Same string in, same cover out — no randomness, nothing stored. */
export function coverSeed(text) {
  let hash = 0
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 100003
  return {
    hues: [hash % 360, (hash * 7) % 360],
    pattern: PATTERN_NAMES[hash % PATTERN_NAMES.length],
  }
}

/**
 * `square` is the record; `circle` is one track off it.
 *
 * The only thing `shape` changes is the corner radius, so a caller that says
 * nothing gets exactly the tile it got before. It exists because the queue
 * lists albums and songs in one ranked list and the two were indistinguishable
 * — same tile, same subtitle, and only the link target telling them apart.
 */
export function cornerRadius(shape, rounded, size) {
  if (shape === 'circle') return '50%'
  return rounded ? Math.max(6, size * 0.09) : 0
}

export function FallbackArt({
  title = '',
  artist = '',
  size = 64,
  rounded = true,
  shape = 'square',
  className,
}) {
  const { hues, pattern } = coverSeed(`${artist}|${title}`)
  const gradientId = `art-${Math.abs(hues[0] * 1000 + hues[1])}-${pattern}`
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={title ? `Generated cover for ${title}` : 'Generated cover'}
      className={className}
      style={{ borderRadius: cornerRadius(shape, rounded, size), flex: 'none' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={`hsl(${hues[0]} 62% 46%)`} />
          <stop offset="100%" stopColor={`hsl(${hues[1]} 68% 30%)`} />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill={`url(#${gradientId})`} />
      {PATTERNS[pattern]()}
    </svg>
  )
}

/**
 * Above this rendered size, ask for the 500px file (A3).
 *
 * The rows are 44–56px and the album hero is 280px, so anything in between
 * would do — but the number that matters is the one a high-DPI screen implies.
 * A 56px row at 3× device pixel ratio wants 168 real pixels, comfortably
 * inside 250, so a phone does not blur. The threshold sits above every row
 * size and below the hero, which means a new row context is correct by
 * default and only something deliberately large opts out.
 */
const SMALL_ENOUGH_FOR_250 = 120

/**
 * The sizes to try, best first.
 *
 * Deduped, because the server applies its own fallback chain before sending:
 * an album whose artwork has no 250px file arrives with `thumbnail_small_url`
 * equal to `thumbnail_url`. Without the dedupe an error would re-request a
 * URL that had just failed before giving up, which is a wasted round trip on
 * exactly the connection that is already struggling.
 */
export function candidateUrls(art, size) {
  if (!art) return []
  const ladder =
    size <= SMALL_ENOUGH_FOR_250
      ? [art.thumbnail_small_url, art.thumbnail_url, art.url]
      : [art.thumbnail_url, art.url]
  return [...new Set(ladder.filter(Boolean))]
}

/**
 * `priority` marks the one cover that is the reason the page exists — the
 * album hero, and nothing else. It is the page's LCP element and it is above
 * the fold by definition, so `loading="lazy"` on it is a deferral that buys
 * nothing: the browser holds the request back until layout settles and then
 * fetches it at low priority, behind requests for things nobody is looking
 * at yet. Every other cover on the site is a 44–56px row that may well be
 * offscreen, and those keep `lazy`, which is what it is for.
 *
 * It matters here more than it would elsewhere because a cover art URL is
 * three round trips to three different hosts (see IMPLEMENTATION.md for the
 * measurements) — whatever the client wastes before starting is added to a
 * second of network it cannot do anything about.
 */
export default function Artwork({
  art,
  title = '',
  artist = '',
  size = 64,
  rounded = true,
  shape = 'square',
  className = '',
  priority = false,
}) {
  const [attempt, setAttempt] = useState(0)
  const candidates = candidateUrls(art, size)

  /**
   * A different album's artwork starts at the top of its own ladder.
   *
   * Adjusted during render rather than in an effect: an effect runs after the
   * commit, so a component reused for another album — a virtualised list, or
   * any call site that stops keying rows by `public_id` — would mount an
   * `<img>` at the old ladder position first and spend a real request on the
   * wrong file. Setting state during render makes React discard this pass and
   * re-render before anything reaches the DOM, so the stale position never
   * becomes a request.
   *
   * Keyed on the candidate list rather than on `art` identity, because a
   * parent hands us a content-equal object on every refetch and resetting
   * then would re-request the URL that had just failed — the same wasted
   * round trip the ladder's dedupe above already guards against.
   */
  const ladderKey = candidates.join('\n')
  const [prevLadderKey, setPrevLadderKey] = useState(ladderKey)
  if (ladderKey !== prevLadderKey) {
    setPrevLadderKey(ladderKey)
    setAttempt(0)
  }

  const src = candidates[attempt] ?? null

  if (!src) {
    return (
      <FallbackArt
        title={title}
        artist={artist}
        size={size}
        rounded={rounded}
        shape={shape}
        className={className}
      />
    )
  }

  return (
    <img
      className={`artwork ${className}`}
      src={src}
      width={size}
      height={size}
      alt={`Cover of ${title}`}
      key={src}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      decoding="async"
      onError={() => setAttempt((n) => n + 1)}
      style={{
        borderRadius: cornerRadius(shape, rounded, size),
        objectFit: 'cover',
        flex: 'none',
      }}
    />
  )
}
